import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Search, Plus, Eye, Edit2, Filter, Download, Car, User } from 'lucide-react';
import { visitorLogService } from '../../services';
import { useLocation } from '../../contexts/LocationContext';
import type { VisitorLog } from '../../types';
import { format } from 'date-fns';

const VisitorLogsPage: React.FC = () => {
  const navigate = useNavigate();
  const { locations, selectedLocationId } = useLocation();
  const [logs, setLogs] = useState<VisitorLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters - initialize filterLocation from global context
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState(selectedLocationId);
  const [filterAccessType, setFilterAccessType] = useState('all');
  const [filterOperatorType, setFilterOperatorType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Sync with global location when it changes
  useEffect(() => {
    setFilterLocation(selectedLocationId);
  }, [selectedLocationId]);

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [filterLocation, filterAccessType, filterOperatorType, startDate, endDate, searchTerm, currentPage]);

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Build filter string for server-side filtering
      const filters: string[] = [];
      
      if (filterLocation !== 'all') {
        filters.push(`location_id::${filterLocation}`);
      }
      
      if (filterAccessType !== 'all') {
        filters.push(`log_access_type::${filterAccessType}`);
      }
      
      if (filterOperatorType !== 'all') {
        filters.push(`log_operator_type::${filterOperatorType}`);
      }
      
      if (searchTerm) {
        filters.push(`log_license_plate:like::${searchTerm}`);
      }
      
      if (startDate) {
        filters.push(`created_date:gte::${startDate}`);
      }
      
      if (endDate) {
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        filters.push(`created_date:lte::${endDateTime.toISOString()}`);
      }
      
      const response = await visitorLogService.getVisitorLogs({ 
        sort: 'log_id,desc',
        size: itemsPerPage,
        page: currentPage,
        filter: filters.length > 0 ? filters.join(',') : undefined
      });
      
      setLogs(response.data || []);
      setTotalItems(response.totalElements || response.data?.length || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const formatDate = (date?: string) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MMM d, h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  const columns = [
    {
      key: 'photo',
      label: 'Photo',
      render: (log: VisitorLog) => (
        <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
          {log.log_image ? (
            <img src={log.log_image} alt="Face" className="w-full h-full object-cover" />
          ) : (
            <User size={20} className="text-gray-400" />
          )}
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (log: VisitorLog) => (
        <Badge 
          variant={log.log_access_type === 'ENTRY' ? 'success' : 'warning'} 
          size="sm"
        >
          {log.log_access_type}
        </Badge>
      ),
    },
    {
      key: 'vehicle',
      label: 'Vehicle',
      render: (log: VisitorLog) => (
        <div>
          <div className="font-medium text-text-primary">{log.log_license_plate || 'N/A'}</div>
          {log.log_vehicle_type && (
            <div className="text-xs text-text-muted">{log.log_vehicle_type}</div>
          )}
          {(log.log_vehicle_make || log.log_vehicle_model) && (
            <div className="text-xs text-text-muted">
              {log.log_vehicle_make} {log.log_vehicle_model}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'truck_trailer',
      label: 'Unit/Trailer',
      render: (log: VisitorLog) => (
        <div className="text-sm">
          {log.log_truck && (
            <div className="text-text-primary">Unit: {log.log_truck}</div>
          )}
          {log.log_trailer && (
            <div className="text-text-muted text-xs">Trailer: {log.log_trailer}</div>
          )}
          {!log.log_truck && !log.log_trailer && (
            <span className="text-text-muted">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'driver',
      label: 'Driver/Visitor',
      render: (log: VisitorLog) => (
        <div>
          <div className="text-sm text-text-primary">
            {log.log_external_driver_name || 'Unknown'}
          </div>
          {log.log_operator_type && (
            <Badge variant="default" size="sm" className="mt-1">
              {log.log_operator_type}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (log: VisitorLog) => (
        <div className="text-sm text-text-secondary">
          {log.location_name || `Location #${log.location_id}`}
        </div>
      ),
    },
    {
      key: 'parking',
      label: 'Parking',
      render: (log: VisitorLog) => (
        <div className="text-sm">
          {log.parking_spot_number ? (
            <>
              <div className="font-medium text-text-primary">
                {log.parking_spot_number_2 ? (
                  <>
                    <span>T: {log.parking_spot_number}</span>
                    <span className="ml-2">Tr: {log.parking_spot_number_2}</span>
                  </>
                ) : (
                  log.parking_spot_number
                )}
              </div>
              {log.parking_lot_name && (
                <div className="text-xs text-text-muted">{log.parking_lot_name}</div>
              )}
            </>
          ) : (
            <span className="text-text-muted">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'time',
      label: 'Time',
      render: (log: VisitorLog) => (
        <div className="text-sm text-text-muted">{formatDate(log.created_date)}</div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (log: VisitorLog) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/entry-exit/${log.log_id}`)}
            title="View"
          >
            <Eye size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/entry-exit/${log.log_id}/edit`)}
            title="Edit"
          >
            <Edit2 size={16} />
          </Button>
        </div>
      ),
    },
  ];

  const locationOptions = [
    { label: 'All Locations', value: 'all' },
    ...locations.map(loc => ({ label: loc.ycl_name, value: loc.ycl_id.toString() })),
  ];

  const accessTypeOptions = [
    { label: 'All Types', value: 'all' },
    { label: 'Entry', value: 'ENTRY' },
    { label: 'Exit', value: 'EXIT' },
  ];

  const operatorTypeOptions = [
    { label: 'All Types', value: 'all' },
    { label: 'Employee', value: 'EMPLOYEE' },
    { label: 'Subcontractor', value: 'SUBCONTRACTOR' },
    { label: 'Visitor', value: 'VISITOR' },
  ];

  const entryCount = logs.filter(l => l.log_access_type === 'ENTRY').length;
  const exitCount = logs.filter(l => l.log_access_type === 'EXIT').length;
  const vehicleCount = logs.filter(l => l.has_vehicle === '1').length;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setFilterLocation('all');
    setFilterAccessType('all');
    setFilterOperatorType('all');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  return (
    <MainLayout>
      <div className="page-stack">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Entry/Exit Logs</h1>
            <p className="page-description">Manage visitor and driver entry/exit records</p>
          </div>
          <div className="page-actions">
            <Button
              variant="outline"
              leftIcon={<Download size={18} />}
            >
              Export
            </Button>
            <Button
              variant="primary"
              leftIcon={<Plus size={18} />}
              onClick={() => navigate('/entry-exit/new')}
            >
              New Log
            </Button>
          </div>
        </div>

        {error && <Alert variant="error" title="Error" message={error} />}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card padding="md">
            <div className="text-text-muted text-sm mb-1">Total Logs</div>
            <div className="text-2xl font-bold text-text-primary">{logs.length}</div>
          </Card>
          <Card padding="md">
            <div className="text-text-muted text-sm mb-1">Entries</div>
            <div className="text-2xl font-bold text-green-600">{entryCount}</div>
          </Card>
          <Card padding="md">
            <div className="text-text-muted text-sm mb-1">Exits</div>
            <div className="text-2xl font-bold text-orange-600">{exitCount}</div>
          </Card>
          <Card padding="md">
            <div className="text-text-muted text-sm mb-1">With Vehicle</div>
            <div className="text-2xl font-bold text-blue-600">{vehicleCount}</div>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18} className="text-text-muted" />
            <h2 className="text-lg font-semibold text-text-primary">Filters</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Input
              label="Search"
              placeholder="License, driver, make, model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={<Search size={18} />}
            />
            <Select
              label="Location"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              options={locationOptions}
            />
            <Select
              label="Access Type"
              value={filterAccessType}
              onChange={(e) => setFilterAccessType(e.target.value)}
              options={accessTypeOptions}
            />
            <Select
              label="Entry/Exit By"
              value={filterOperatorType}
              onChange={(e) => setFilterOperatorType(e.target.value)}
              options={operatorTypeOptions}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Start Date"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              label="End Date"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={handleResetFilters}
                fullWidth
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card padding="none">
          {isLoading ? (
            <div className="p-8">
              <Loading size="lg" text="Loading logs..." />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-text-muted">No logs found</p>
            </div>
          ) : (
            <>
              <Table columns={columns} data={logs} isLoading={isLoading} />
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-border-primary flex items-center justify-between">
                  <div className="text-sm text-text-muted">
                    Page {currentPage} of {totalPages} ({totalItems} total logs)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    
                    {/* Page Numbers */}
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? 'primary' : 'outline'}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </MainLayout>
  );
};

export default VisitorLogsPage;
